import FormField from './FormField';
import FormWrapper from './FormWrapper';
import CredentialSecurityNotice from './CredentialSecurityNotice';
import ConnectionTestButton from './ConnectionTestButton';
import type { FieldConfig } from './types';
import type { SnowflakeConfig } from '../../types';
import { transformSnowflakeConfigForTest } from '../../utils/connectionConfigTransformers';
import { useConnectionTest } from '../../hooks/useConnectionTest';

const fields: FieldConfig[] = [
    {
        name: 'account',
        label: 'Account',
        type: 'text',
        required: true,
        placeholder: 'xy12345.us-east-1',
        extra: 'Example: xy12345.us-east-1',
    },
    {
        name: 'warehouse',
        label: 'Warehouse',
        type: 'text',
        required: true,
        placeholder: 'COMPUTE_WH',
    },
    {
        name: 'database',
        label: 'Database',
        type: 'text',
        required: true,
        placeholder: 'MY_DATABASE',
    },
    {
        name: 'schema',
        label: 'Schema',
        type: 'text',
        required: true,
        placeholder: 'PUBLIC',
    },
    {
        name: 'username',
        label: 'Username',
        type: 'text',
        required: true,
        placeholder: 'Enter username',
        isCredential: true,
    },
    {
        name: 'password',
        label: 'Password',
        type: 'password',
        required: true,
        placeholder: 'Enter password',
        isCredential: true,
    },
    {
        name: 'stageName',
        label: 'Stage Name',
        type: 'text',
        required: true,
        placeholder: 'my_stage',
        extra: 'Snowflake stage where files are stored',
    },
    {
        name: 'filename',
        label: 'Filename',
        type: 'text',
        required: true,
        placeholder: 'data.zip',
        extra: 'File to fetch from the stage',
    },
    {
        name: 'role',
        label: 'Role (Optional)',
        type: 'text',
        placeholder: 'ACCOUNTADMIN',
    },
];

interface SnowflakeConfigFormProps {
    onConnectionTestSuccess?: () => void;
}

export default function SnowflakeConfigForm({ onConnectionTestSuccess }: SnowflakeConfigFormProps) {
    const { getConfig, handleTestConnection, isLoading } = useConnectionTest<SnowflakeConfig>(
        transformSnowflakeConfigForTest,
        config =>
            Boolean(
                config?.account &&
                config?.warehouse &&
                config?.database &&
                config?.schema &&
                config?.username &&
                config?.password &&
                config?.stageName &&
                config?.filename
            ),
        onConnectionTestSuccess
    );

    return (
        <FormWrapper>
            <CredentialSecurityNotice />
            {fields.map(field => (
                <FormField
                    key={Array.isArray(field.name) ? field.name.join('.') : field.name}
                    config={field}
                />
            ))}
            {onConnectionTestSuccess && (
                <ConnectionTestButton
                    getConfig={getConfig}
                    testConnection={handleTestConnection}
                    isLoading={isLoading}
                />
            )}
        </FormWrapper>
    );
}
